import { useState, useEffect } from 'react'
import s from './OpportunityByGeoArea.scss'
import {
  makeReportsPostRequest,
  getReportsBaseUrl,
  makePatchRequest,
  makeGetRequest
} from 'utils/api'
import { toUsers, toCategories, toPieChartData } from './serializers'
import { useLoading } from 'hooks'
import { getCurrency, getToken } from 'utils/authService'
import MaterialTable from '@material-table/core'
import { NumericFormat } from 'react-number-format'
import { RangeDatePicker, SwitchToggle } from 'components/ideal-core-ui'
import { utcDate, timezoneDate, currencyFormatter } from 'utils/helpers'
import { FaFileExcel, FaAngleRight, FaAngleDown, FaInfoCircle } from 'react-icons/fa'
import moment from 'moment'
import { AddButton } from 'components/common/AddButton'
import {
  Card,
  CardBody,
  CardHeader,
  Collapse,
  UncontrolledTooltip,
  Container,
  Row,
  Col
} from 'reactstrap'
import currency from 'currency.js'
import Select from 'react-select'
import { toTeams } from 'utils/serializers'
import { BID_SUBMISSION_STATUS } from 'utils/constants'
import { ResponsivePie } from '@nivo/pie'
import { ExportCsv, ExportPdf } from '@material-table/exporters'

const OpportunityByGeoArea = () => {
  const [result, setResult] = useState([])
  const [piesResult, setPiesResult] = useState({})
  const [users, setUsers] = useState([])
  const [userFilter, setUserFilter] = useState([])
  const [teamFilter, setTeamFilter] = useState([])
  const [teams, setTeams] = useState([])
  const [contractTypes, setContractTypes] = useState([])
  const [, withLoadingLocks] = useLoading()
  const [collapse, setCollapse] = useState('')
  const [wonDateEnabled, setWonDateEnabled] = useState(false)
  const [wonStartDate, setWonStartDate] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [wonEndDate, setWonEndDate] = useState(moment().add(6, 'weeks').endOf('day').format())
  const [lostDateEnabled, setLostDateEnabled] = useState(false)
  const [lostStartDate, setLostStartDate] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [lostEndDate, setLostEndDate] = useState(moment().add(6, 'weeks').endOf('day').format())
  const [bidDueDateEnabled, setBidDueDateEnabled] = useState(false)
  const [bidDueStartDate, setBidDueStartDate] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [bidDueEndDate, setBidDueEndDate] = useState(moment().add(6, 'weeks').endOf('day').format())
  const [followUpDateEnabled, setFollowUpDateEnabled] = useState(false)
  const [followUpStartDate, setFollowUpStartDate] = useState(
    moment().subtract(2, 'weeks').startOf('day').format()
  )
  const [followUpEndDate, setFollowUpEndDate] = useState(
    moment().add(6, 'weeks').endOf('day').format()
  )

  const setWonDateFilters = (dates, utc = true) => {
    setWonStartDate(utc ? utcDate(dates.from) : dates.from)
    setWonEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const setLostDateFilters = (dates, utc = true) => {
    setLostStartDate(utc ? utcDate(dates.from) : dates.from)
    setLostEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const setBidDueDateFilters = (dates, utc = true) => {
    setBidDueStartDate(utc ? utcDate(dates.from) : dates.from)
    setBidDueEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  const setFollowUpDateFilters = (dates, utc = true) => {
    setFollowUpStartDate(utc ? utcDate(dates.from) : dates.from)
    setFollowUpEndDate(utc ? utcDate(dates.to) : dates.to)
  }

  useEffect(() => {
    ;(async () => {
      const { content } = await makeGetRequest('/v1/users')
      setUsers(toUsers(content))

      const teams = await makeGetRequest('/v1/teams')
      setTeams(toTeams(teams))

      const types = await makeGetRequest('/v1/geographical-area')
      setContractTypes(toCategories(types))
    })()
  }, [])

  useEffect(() => {
    if (!result || !contractTypes.length) return
    setPiesResult(toPieChartData(result, contractTypes))
  }, [result, contractTypes])

  const columns = [
    {
      title: 'Geo Area',
      field: 'geographicalAreaName',
      width: '400px',
      sorting: true,
      emptyValue: '(empty)',
      editComponent: props => {
        return (
          <Select
            options={contractTypes}
            className={s.typesSelect}
            onChange={async v => {
              await makePatchRequest(`/v1/deals/${props.rowData.id}`, {
                geographicalAreaId: v.value
              })
              refreshReport()
            }}
          />
        )
      }
    },
    {
      title: 'Bid Due Date',
      field: 'bidDueDate',
      sorting: true,
      width: '120px',
      editable: 'never',
      defaultSort: 'asc',
      render: row => timezoneDate(row.bidDueDate),
      exportTransformer: row => timezoneDate(row.bidDueDate)
    },
    {
      title: 'Follow-up Date',
      field: 'bidFollowUpDate',
      sorting: true,
      editable: 'never',
      width: '120px',
      render: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null),
      exportTransformer: row => (row.bidFollowUpDate ? timezoneDate(row.bidFollowUpDate) : null)
    },
    {
      title: 'Quote #',
      field: 'quoteNumber',
      width: '150px',
      sorting: true,
      editable: 'never'
    },
    {
      title: 'Project #',
      field: 'projectNumber',
      width: '150px',
      sorting: true,
      editable: 'never'
    },
    {
      title: 'Opportunity Status',
      field: 'status',
      width: '200px',
      sorting: true,
      editable: 'never'
    },
    {
      title: 'Opportunity Name',
      field: 'name',
      width: '150px',
      editable: 'never',
      sorting: true,
      render: row => (
        <a href={`/opportunities/${row.id}`} target="_blank" rel="noreferrer">
          {row.name}
        </a>
      )
    },
    {
      title: 'Submission Status',
      field: 'submissionStatus',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => BID_SUBMISSION_STATUS[row.submissionStatus],
      exportTransformer: row => BID_SUBMISSION_STATUS[row.submissionStatus]
    },
    {
      title: 'Client(s)',
      field: 'companies',
      sorting: true,
      width: '200px',
      editable: 'never',
      render: row => row.companies.join(', '),
      exportTransformer: row => row.companies.join(', ')
    },
    {
      title: 'Contact(s)',
      field: 'contacts',
      sorting: true,
      width: '400px',
      editable: 'never',
      render: row => {
        return row.contacts
          .map(
            c =>
              `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
                c.cellPhone ? c.cellPhone + ', ' : ''
              }${c.email}`
          )
          .join(' | ')
      },
      exportTransformer: row => {
        return row.contacts
          .map(
            c =>
              `${c.company}, ${c.name}, ${c.phone ? c.phone + ', ' : ''}${
                c.cellPhone ? c.cellPhone + ', ' : ''
              }${c.email}`
          )
          .join(' | ')
      }
    },
    {
      title: 'Awarded To',
      field: 'awardedCompany',
      width: '200px',
      editable: 'never',
      sorting: true
    },
    {
      title: 'Bid Value',
      field: 'value',
      width: '120px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.value}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.value })
    },
    {
      title: 'Won Value',
      field: 'wonValue',
      width: '120px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.wonValue}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.wonValue })
    },
    {
      title: 'Cost',
      field: 'const',
      width: '120px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.cost}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.cost })
    },
    {
      title: 'Profit',
      field: 'grossProfit',
      width: '120px',
      sorting: true,
      editable: 'never',
      render: row => (
        <NumericFormat
          thousandSeparator
          decimalScale={2}
          value={row.grossProfit}
          prefix={getCurrency().symbol}
          displayType="text"
        />
      ),
      exportTransformer: row => currencyFormatter({ value: row.grossProfit })
    },
    {
      title: 'Team(s)',
      field: 'teams',
      width: '250px',
      sorting: true,
      editable: 'never',
      render: row => row.teams?.join(', '),
      exportTransformer: row => row.teams?.join(', ')
    },
    {
      title: 'Opportunity Manager',
      field: 'manager',
      width: '150px',
      editable: 'never',
      sorting: true
    }
  ]

  async function refreshReport() {
    const result = await withLoadingLocks(
      makeReportsPostRequest('/v1/deals/geographical-area', {
        ...(wonDateEnabled
          ? {
              wonDateFrom: wonStartDate,
              wonDateTo: wonEndDate
            }
          : {}),
        ...(lostDateEnabled
          ? {
              lostDateFrom: lostStartDate,
              lostDateTo: lostEndDate
            }
          : {}),
        ...(bidDueDateEnabled
          ? {
              bidDueDateFrom: bidDueStartDate,
              bidDueDateTo: bidDueEndDate
            }
          : {}),
        ...(followUpDateEnabled
          ? {
              followUpDateFrom: followUpStartDate,
              followUpDateTo: followUpEndDate
            }
          : {}),
        ...(userFilter && userFilter.length
          ? { userFilter: userFilter.map(user => user.value) }
          : {}),
        ...(teamFilter && teamFilter.length
          ? { teamFilter: teamFilter.map(user => user.value) }
          : {})
      })
    )
    setResult(result)
  }

  useEffect(() => {
    refreshReport()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    wonDateEnabled,
    wonStartDate,
    wonEndDate,
    userFilter,
    teamFilter,
    lostDateEnabled,
    lostStartDate,
    lostEndDate,
    bidDueDateEnabled,
    bidDueStartDate,
    bidDueEndDate,
    followUpDateEnabled,
    followUpStartDate,
    followUpEndDate
  ])

  const onBtnExport = () => {
    window.open(
      getReportsBaseUrl(
        `/v1/deals/geographical-area/export?userFilter=${userFilter
          .map(user => user.value)
          .join()}&teamFilter=${teamFilter.map(user => user.value).join()}&token=${getToken()}${
          wonDateEnabled ? '&wonDateFrom=' + wonStartDate + '&wonDateTo=' + wonEndDate : ''
        }${lostDateEnabled ? '&lostDateFrom=' + lostStartDate + '&lostDateTo=' + lostEndDate : ''}${
          bidDueDateEnabled
            ? '&bidDueDateFrom=' + bidDueStartDate + '&bidDueDateTo=' + bidDueEndDate
            : ''
        }${
          followUpDateEnabled
            ? '&followUpDateFrom=' + followUpStartDate + '&followUpDateTo=' + followUpEndDate
            : ''
        }`
      ),
      '_blank'
    )
  }

  const toggleCollapse = stage => {
    if (collapse !== stage) {
      setCollapse(stage)
    } else {
      setCollapse('')
    }
  }

  return (
    <>
      <h6 className={s.title}>Sales Reports &gt; Opportunities by Geographical Area</h6>
      <div className={s.filters}>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setBidDueDateEnabled}>
            Bid Due Date
          </SwitchToggle>
          <RangeDatePicker
            startDate={bidDueStartDate}
            endDate={bidDueEndDate}
            onChange={setBidDueDateFilters}
            inputProps={{ disabled: !bidDueDateEnabled }}
          />
        </div>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setFollowUpDateEnabled}>
            Follow-up Date
          </SwitchToggle>
          <RangeDatePicker
            startDate={followUpStartDate}
            endDate={followUpEndDate}
            onChange={setFollowUpDateFilters}
            inputProps={{ disabled: !followUpDateEnabled }}
          />
        </div>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setLostDateEnabled}>
            Lost Date
          </SwitchToggle>
          <RangeDatePicker
            startDate={lostStartDate}
            endDate={lostEndDate}
            onChange={setLostDateFilters}
            inputProps={{ disabled: !lostDateEnabled }}
          />
        </div>
        <div className={s.filter}>
          <SwitchToggle noText onToggle={setWonDateEnabled}>
            Won Date
          </SwitchToggle>
          <RangeDatePicker
            startDate={wonStartDate}
            endDate={wonEndDate}
            onChange={setWonDateFilters}
            inputProps={{ disabled: !wonDateEnabled }}
          />
        </div>
        <Select
          onChange={values => setUserFilter(values)}
          options={users}
          value={userFilter}
          placeholder="Select User(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type1" style={{ position: 'relative', top: 34, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type1">
          To see all opportunities, please remove all users from selection.
        </UncontrolledTooltip>
        <Select
          onChange={values => setTeamFilter(values)}
          options={teams}
          value={teamFilter}
          placeholder="Select Team(s)"
          className={s.userSelect}
          isMulti
        />
        <FaInfoCircle id="Type2" style={{ position: 'relative', top: 34, left: 5 }} />
        <UncontrolledTooltip placement="bottom" target="Type2">
          To see all opportunities, please remove all teams from selection.
        </UncontrolledTooltip>
      </div>
      {Object.keys(result).length > 0 ? (
        <>
          <Container>
            <Row>
              <Col lg={6}>
                <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
                  Total: {piesResult?.totalCount ?? 0} opportunities &#8226; Bid Value:{' '}
                  {currency(piesResult?.totalBidValue, {
                    symbol: getCurrency().symbol
                  }).format()}
                </p>
                <p
                  style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold', textIndent: -60 }}
                >
                  By {getCurrency().symbol} Bid Value
                </p>
                <div className={s.chart}>
                  <ResponsivePie
                    data={piesResult?.pies?.pieByBidValue ?? []}
                    margin={{ top: 20, right: 90, bottom: 50, left: 30 }}
                    padAngle={0.7}
                    cornerRadius={3}
                    fit={false}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLinkLabels={false}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: 'color' }}
                    enableArcLabels={false}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                    tooltip={({ datum }) => {
                      const { id, value } = datum
                      return (
                        <span>
                          {id}:{' '}
                          <strong>
                            <NumericFormat
                              thousandSeparator
                              decimalScale={2}
                              value={value}
                              prefix={getCurrency().symbol}
                              displayType="text"
                            />
                          </strong>
                        </span>
                      )
                    }}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'column',
                        justify: false,
                        translateX: 200,
                        translateY: -90,
                        itemsSpacing: 0,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        itemOpacity: 1,
                        symbolSize: 10,
                        symbolShape: 'square',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemTextColor: '#000'
                            }
                          }
                        ]
                      }
                    ]}
                  />
                </div>
              </Col>
              <Col lg={6}>
                <p style={{ fontWeight: 'bold', textAlign: 'center' }}>
                  Total Won: {piesResult?.wonCount ?? 0} opportunities &#8226; Won Value:{' '}
                  {currency(piesResult?.totalWonValue, {
                    symbol: getCurrency().symbol
                  }).format()}
                </p>
                <p
                  style={{ textAlign: 'center', fontSize: 12, fontWeight: 'bold', textIndent: -60 }}
                >
                  By {getCurrency().symbol} Won Value
                </p>
                <div className={s.chart}>
                  <ResponsivePie
                    data={piesResult?.pies?.pieByWonValue ?? []}
                    margin={{ top: 20, right: 90, bottom: 50, left: 30 }}
                    padAngle={0.7}
                    cornerRadius={3}
                    fit={false}
                    activeOuterRadiusOffset={8}
                    borderWidth={1}
                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                    enableArcLinkLabels={false}
                    arcLinkLabelsSkipAngle={10}
                    arcLinkLabelsThickness={2}
                    arcLinkLabelsColor={{ from: 'color' }}
                    enableArcLabels={false}
                    arcLabelsSkipAngle={10}
                    arcLabelsTextColor={{ from: 'color', modifiers: [['darker', 2]] }}
                    tooltip={({ datum }) => {
                      const { id, value } = datum
                      return (
                        <span>
                          {id}:{' '}
                          <strong>
                            <NumericFormat
                              thousandSeparator
                              decimalScale={2}
                              value={value}
                              prefix={getCurrency().symbol}
                              displayType="text"
                            />
                          </strong>
                        </span>
                      )
                    }}
                    legends={[
                      {
                        anchor: 'bottom',
                        direction: 'column',
                        justify: false,
                        translateX: 200,
                        translateY: -90,
                        itemsSpacing: 0,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemTextColor: '#999',
                        itemDirection: 'left-to-right',
                        itemOpacity: 1,
                        symbolSize: 10,
                        symbolShape: 'square',
                        effects: [
                          {
                            on: 'hover',
                            style: {
                              itemTextColor: '#000'
                            }
                          }
                        ]
                      }
                    ]}
                  />
                </div>
              </Col>
            </Row>
          </Container>
          <div className={s.table}>
            <AddButton className={s.exportExcel} onClick={onBtnExport}>
              <FaFileExcel className={s.excelIcon} />
              Export to Excel
            </AddButton>
            {Object.keys(result).map(stage => (
              <Card className={s.card} key={stage}>
                <CardHeader onClick={() => toggleCollapse(stage)} data-event={stage}>
                  <span className={s.arrow}>
                    {collapse === stage ? <FaAngleRight /> : <FaAngleDown />}
                  </span>
                  {stage} ({result[stage].count}) &#8226; Bid:{' '}
                  {currency(result[stage].totalBidValue, { symbol: getCurrency().symbol }).format()}{' '}
                  &#8226; Won:{' '}
                  {currency(result[stage].totalWonValue, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Cost:{' '}
                  {currency(result[stage].totalCostValue, {
                    symbol: getCurrency().symbol
                  }).format()}{' '}
                  &#8226; Net Profit:{' '}
                  {currency(result[stage].totalProfitValue, {
                    symbol: getCurrency().symbol
                  }).format()}
                </CardHeader>
                <Collapse isOpen={collapse === stage}>
                  <CardBody>
                    {collapse === stage && (
                      <MaterialTable
                        title=""
                        key={stage}
                        columns={columns}
                        data={result[stage].deals}
                        options={{
                          tableLayout: 'fixed',
                          sorting: true,
                          headerStyle: {
                            backgroundColor: '#eee',
                            color: '#000',
                            fontSize: '14px'
                          },
                          pageSize: 10,
                          padding: 'dense',
                          pageSizeOptions: [
                            5,
                            10,
                            20,
                            50,
                            { value: result[stage].deals.length, label: 'All' }
                          ],
                          emptyRowsWhenPaging: false,
                          exportMenu: [
                            {
                              label: 'Export PDF',
                              exportFunc: (cols, datas) =>
                                ExportPdf(cols, datas, 'Opportunities by Geographical Area')
                            },
                            {
                              label: 'Export CSV',
                              exportFunc: (cols, datas) =>
                                ExportCsv(cols, datas, 'Opportunities by Geographical Area')
                            }
                          ],
                          columnsButton: true
                        }}
                        style={{
                          boxShadow: 'none',
                          fontSize: '13px'
                        }}
                        cellEditable={{
                          onCellEditApproved: () => {
                            return new Promise(resolve => resolve())
                          }
                        }}
                      />
                    )}
                  </CardBody>
                </Collapse>
              </Card>
            ))}
          </div>
        </>
      ) : (
        <p className={s.noResults}>No results to display. Try a different time range.</p>
      )}
    </>
  )
}

export default OpportunityByGeoArea
